/**
 * /moniteur/ma-fiche — Fiche publique du moniteur avec QR de rattachement (Phase 31)
 *
 * L'enseignant :
 *   • édite son nom affiché, photo (URL), auto-école, bio, années d'expérience, spécialités
 *   • active/désactive chaque section publiquement (show_*)
 *   • copie l'URL de sa fiche publique / imprime son QR
 *   • régénère son QR si compromis
 *   • désactive temporairement son QR
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  QrCode, Save, Loader2, RefreshCw, Eye, EyeOff, Copy, Check, ExternalLink, Printer,
  User, Building2, BookOpen, Award, MessageSquare, AlertCircle, CheckCircle2, Link as LinkIcon,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../../api';
import { toast } from 'sonner';
import MaVCardQuickAccess from '../../components/MaVCardQuickAccess';

const FORMULES = [
  { key: 'permis_b', label: 'Permis B' },
  { key: 'aac', label: 'AAC' },
  { key: 'permis_b78', label: 'B78 (BEA)' },
  { key: 'permis_a1', label: 'A1 (125)' },
  { key: 'permis_a2', label: 'A2 (moto)' },
  { key: 'passerelle_a', label: 'Passerelle A' },
  { key: 'formation_125', label: 'Formation 125' },
  { key: 'code_seul', label: 'Code seul' },
];

export default function MaFichePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await api.moniteurProfileGetMe();
      setProfile(p);
    } catch (e) {
      toast.error('Impossible de charger votre fiche', { description: e.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = 'Ma fiche moniteur · Espace enseignant';
    load();
  }, [load]);

  const patch = async (updates) => {
    setSaving(true);
    try {
      const p = await api.moniteurProfilePatchMe(updates);
      setProfile(p);
    } catch (e) {
      toast.error('Enregistrement échoué', { description: e.message });
    }
    setSaving(false);
  };

  const publicUrl = profile
    ? `${window.location.origin}/moniteur/qr/${profile.qr_token}`
    : '';

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Lien copié');
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      toast.error('Impossible de copier');
    }
  };

  const toggleQr = async () => {
    try {
      const r = await api.moniteurProfileToggleQr();
      setProfile((p) => ({ ...p, qr_active: r.qr_active }));
      toast.success(r.qr_active ? 'QR réactivé' : 'QR désactivé');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const regenerate = async () => {
    try {
      const r = await api.moniteurProfileRegenerateQr();
      setProfile((p) => ({ ...p, qr_token: r.qr_token }));
      setRegenOpen(false);
      toast.success('Nouveau QR généré', {
        description: 'Ton ancien QR ne fonctionne plus. Réimprime-le pour tes élèves.',
      });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const printQr = () => {
    if (!qrRef.current || !profile) return;
    const canvas = qrRef.current.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const html = `
<!doctype html><html><head><meta charset="utf-8"><title>QR ${profile.display_name}</title>
<style>
  body{margin:0;padding:40px;font-family:'Playfair Display',Georgia,serif;background:#fff;color:#111;text-align:center}
  h1{font-size:32px;font-weight:900;margin:0 0 4px}
  .sub{color:#666;font-size:14px;margin-bottom:36px}
  .qr{display:inline-block;padding:24px;border:2px solid #111;border-radius:16px}
  .cta{font-size:20px;font-weight:800;margin-top:32px}
  .brand{font-size:11px;letter-spacing:0.3em;color:#e11d48;text-transform:uppercase;margin-bottom:6px}
</style></head>
<body>
  <div class="brand">code2roues.fr · Moniteur</div>
  <h1>${profile.display_name}</h1>
  ${profile.auto_ecole_name ? `<div class="sub">${profile.auto_ecole_name}</div>` : '<div class="sub">Ta fiche moniteur</div>'}
  <div class="qr"><img src="${dataUrl}" width="360" height="360" alt="QR"></div>
  <div class="cta">Scanne pour me rejoindre comme moniteur</div>
  <div style="font-size:11px;color:#999;margin-top:32px">${publicUrl}</div>
</body></html>`;
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) { toast.error('Popup bloquée — autorisez-la puis réessayez'); return; }
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 350);
  };

  const toggleSpecialty = (key) => {
    const cur = profile.specialties || [];
    const next = cur.includes(key) ? cur.filter((s) => s !== key) : [...cur, key];
    patch({ specialties: next });
  };

  if (loading || !profile) {
    return <div className="text-white/40 text-sm">Chargement de votre fiche…</div>;
  }

  return (
    <div className="max-w-5xl" data-testid="ma-fiche-page">
      <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">Enseignant · Ma fiche publique</div>
      <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Ma fiche moniteur</h1>
      <p className="text-sm text-white/60 mt-2 mb-6">
        Ta carte de visite publique. Les élèves qui scannent ton QR arrivent sur cette fiche et peuvent te rejoindre en un clic.
      </p>

      {/* ═══ Carte de visite digitale (démarchage prospects) ═══ */}
      <div className="mb-6">
        <MaVCardQuickAccess compact />
      </div>

      {/* ═══ Bloc QR ═══════════════════════════════════════════════════════ */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-6" data-testid="qr-section">
        <div className="grid md:grid-cols-[240px_1fr] gap-6 items-start">
          <div className="text-center">
            <div ref={qrRef} className={`inline-block p-3 rounded-2xl border ${profile.qr_active ? 'bg-white border-white/20' : 'bg-white/40 border-white/10 grayscale opacity-60'}`}>
              <QRCodeCanvas value={publicUrl} size={200} level="M" includeMargin={false} />
            </div>
            <div className="mt-3">
              {profile.qr_active ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-semibold uppercase tracking-widest">
                  <CheckCircle2 size={11} /> Actif
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 font-semibold uppercase tracking-widest">
                  <EyeOff size={11} /> Désactivé
                </span>
              )}
            </div>
          </div>

          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-1">URL publique de ta fiche</div>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 min-w-0 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-[12px] text-white/80 truncate font-mono" data-testid="public-url">
                {publicUrl}
              </div>
              <button
                onClick={copyUrl}
                data-testid="btn-copy-url"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/80 text-[12px] font-semibold transition"
              >
                {copied ? <><Check size={13} className="text-emerald-400" /> Copié</> : <><Copy size={13} /> Copier</>}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/80 text-[12px] font-semibold transition"
                data-testid="btn-preview-public"
              >
                <ExternalLink size={13} /> Aperçu
              </a>
              <button
                onClick={printQr}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/80 text-[12px] font-semibold transition"
                data-testid="btn-print-qr"
              >
                <Printer size={13} /> Imprimer
              </button>
              <button
                onClick={toggleQr}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border text-[12px] font-semibold transition ${
                  profile.qr_active
                    ? 'border-amber-500/30 bg-amber-500/[0.08] text-amber-300 hover:bg-amber-500/[0.14]'
                    : 'border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-300 hover:bg-emerald-500/[0.14]'
                }`}
                data-testid="btn-toggle-qr"
              >
                {profile.qr_active ? <><EyeOff size={13} /> Désactiver</> : <><Eye size={13} /> Réactiver</>}
              </button>
              <button
                onClick={() => setRegenOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-rose-500/30 bg-rose-500/[0.08] hover:bg-rose-500/[0.14] text-rose-300 text-[12px] font-semibold transition"
                data-testid="btn-regen-qr"
              >
                <RefreshCw size={13} /> Régénérer
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Scans</div>
                <div className="text-xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{profile.scan_count || 0}</div>
              </div>
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Rejoint</div>
                <div className="text-xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{profile.join_count || 0}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Édition de la fiche ═══════════════════════════════════════════ */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-6" data-testid="profile-edit-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[10px] font-black uppercase tracking-widest text-rose-400">Contenu de ma fiche</h2>
          {saving && <span className="inline-flex items-center gap-1.5 text-[11px] text-white/50"><Loader2 size={11} className="animate-spin" /> Enregistrement…</span>}
        </div>

        {/* Nom affiché */}
        <Field
          icon={User}
          label="Nom affiché"
          required
          value={profile.display_name || ''}
          onBlur={(v) => v !== profile.display_name && v.trim().length >= 2 && patch({ display_name: v.trim() })}
          testid="input-display-name"
        />

        {/* Photo (URL) — toggle */}
        <FieldWithToggle
          icon={User}
          label="Photo (URL)"
          hint="Colle une URL d'image (photo pro, profil)"
          showKey="show_photo"
          profile={profile}
          onToggle={(v) => patch({ show_photo: v })}
        >
          <input
            type="url"
            defaultValue={profile.photo_url || ''}
            placeholder="https://…/moi.jpg"
            onBlur={(e) => e.target.value !== profile.photo_url && patch({ photo_url: e.target.value.trim() })}
            data-testid="input-photo-url"
            className="w-full h-10 px-3 rounded-lg border border-white/10 bg-black/30 text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60"
          />
        </FieldWithToggle>

        {/* Auto-école — toggle */}
        <FieldWithToggle
          icon={Building2}
          label="Auto-école"
          showKey="show_auto_ecole"
          profile={profile}
          onToggle={(v) => patch({ show_auto_ecole: v })}
        >
          <input
            type="text"
            defaultValue={profile.auto_ecole_name || ''}
            placeholder="ex. Auto-École du Centre"
            onBlur={(e) => e.target.value !== profile.auto_ecole_name && patch({ auto_ecole_name: e.target.value.trim() })}
            data-testid="input-auto-ecole"
            className="w-full h-10 px-3 rounded-lg border border-white/10 bg-black/30 text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60"
          />
        </FieldWithToggle>

        {/* Années d'expérience — toggle */}
        <FieldWithToggle
          icon={Award}
          label="Années d'expérience"
          showKey="show_experience"
          profile={profile}
          onToggle={(v) => patch({ show_experience: v })}
        >
          <input
            type="number"
            min={0}
            max={60}
            defaultValue={profile.years_experience ?? ''}
            placeholder="15"
            onBlur={(e) => {
              const n = e.target.value === '' ? null : Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0));
              if (n !== profile.years_experience) patch({ years_experience: n });
            }}
            data-testid="input-experience"
            className="w-32 h-10 px-3 rounded-lg border border-white/10 bg-black/30 text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60"
          />
        </FieldWithToggle>

        {/* Bio — toggle */}
        <FieldWithToggle
          icon={MessageSquare}
          label="Bio courte"
          hint="Max 800 caractères"
          showKey="show_bio"
          profile={profile}
          onToggle={(v) => patch({ show_bio: v })}
        >
          <textarea
            defaultValue={profile.bio || ''}
            placeholder="Présente-toi en quelques lignes…"
            maxLength={800}
            rows={3}
            onBlur={(e) => e.target.value !== profile.bio && patch({ bio: e.target.value.trim() })}
            data-testid="input-bio"
            className="w-full px-3 py-2 rounded-lg border border-white/10 bg-black/30 text-white text-[13px] placeholder-white/25 focus:outline-none focus:border-rose-500/60 resize-none"
          />
        </FieldWithToggle>

        {/* Spécialités — toggle */}
        <FieldWithToggle
          icon={BookOpen}
          label="Spécialités"
          hint="Coche les permis / formations que tu enseignes"
          showKey="show_specialties"
          profile={profile}
          onToggle={(v) => patch({ show_specialties: v })}
        >
          <div className="flex flex-wrap gap-1.5">
            {FORMULES.map((f) => {
              const active = (profile.specialties || []).includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => toggleSpecialty(f.key)}
                  data-testid={`chip-spec-${f.key}`}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${
                    active
                      ? 'bg-rose-600 border-rose-500 text-white'
                      : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.08]'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </FieldWithToggle>
      </section>

      {/* ═══ Modale régénération QR ═══════════════════════════════════════ */}
      {regenOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setRegenOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111112] p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            data-testid="regen-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0">
                <AlertCircle size={18} />
              </div>
              <div>
                <h3 className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Régénérer ton QR ?</h3>
                <p className="text-[13px] text-white/60 mt-1">
                  L&apos;ancien QR sera <strong>immédiatement invalide</strong>. Tous les posters/flyers imprimés avec l&apos;ancien
                  QR devront être remplacés. Les élèves déjà rattachés ne sont pas impactés.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setRegenOpen(false)}
                className="px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white/60 hover:text-white hover:bg-white/[0.06] transition"
              >
                Annuler
              </button>
              <button
                onClick={regenerate}
                data-testid="btn-confirm-regen"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-[13px] font-semibold transition"
              >
                <RefreshCw size={14} /> Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sous-composants ─────────────────────────────────────────────────────
function Field({ icon: Icon, label, required, value, onBlur, testid }) {
  return (
    <div className="mb-4">
      <label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">
        <Icon size={12} /> {label}
        {required && <span className="text-rose-400">*</span>}
      </label>
      <input
        type="text"
        defaultValue={value}
        onBlur={(e) => onBlur(e.target.value)}
        data-testid={testid}
        className="w-full h-10 px-3 rounded-lg border border-white/10 bg-black/30 text-white text-[13px] focus:outline-none focus:border-rose-500/60"
      />
    </div>
  );
}

function FieldWithToggle({ icon: Icon, label, hint, showKey, profile, onToggle, children }) {
  const isOn = !!profile[showKey];
  return (
    <div className="mb-5 rounded-xl border border-white/[0.06] bg-white/[0.01] p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/60">
            <Icon size={12} /> {label}
          </div>
          {hint && <div className="text-[10.5px] text-white/40 mt-0.5">{hint}</div>}
        </div>
        <button
          type="button"
          onClick={() => onToggle(!isOn)}
          data-testid={`toggle-${showKey}`}
          className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider transition ${
            isOn
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
              : 'bg-white/[0.04] text-white/40 border border-white/10'
          }`}
        >
          {isOn ? <><Eye size={11} /> Public</> : <><EyeOff size={11} /> Privé</>}
        </button>
      </div>
      {children}
    </div>
  );
}
